import type { ErroreRicevuto, Errori } from './regole-anagrafica.ts'

/**
 * Regole dei consensi della Scheda utente Rev05 (D-08, D-09), senza dipendenze dal browser: forme
 * dei dati di GET /consensi/modulo e GET /persone/:id/consensi, stato e verifiche dei moduli di
 * sottoscrizione e di revoca, corpi delle richieste, errori dell'API riportati sul campo.
 *
 * Le verifiche ripetono quelle di apps/api/src/rotte/consensi.ts con gli stessi messaggi e gli
 * stessi nomi di campo; la prova API esegue questo modulo contro le rotte.
 */

export const TIPI_REV05 = ['TRATTAMENTO_DATI', 'CATEGORIE_PARTICOLARI', 'PIATTAFORME_WEB', 'IMMAGINE'] as const
export type TipoRev05 = (typeof TIPI_REV05)[number]

export type Esito = 'espresso' | 'negato'
export type EsitoVigente = Esito | 'revocato' | 'mancante'

export type DichiarazioneModulo = {
  tipo: TipoRev05; numero: number; nome: string; testo: string; nota: string | null
  facoltativo: boolean; necessario_interventi: boolean
}

export type ModuloConsensi = {
  versione: string; codice: string; revisione: string; data_revisione: string; titolo: string
  titolo_sezione: string; informativa: string; dichiarazione: string
  consensi: DichiarazioneModulo[]
}

export type ConsensoLetto = {
  id: string; tipo: string; numero: number | null; nome: string | null; rev05: boolean
  stato: string; data_consenso: string | null; revocato_il: string | null; luogo: string | null
  firmatario: string | null; versione_informativa: string | null; modalita: string | null
  sottoscrizione_id: string | null; note: string | null
  /** Modulo firmato collegato; `valido`: non cancellato, non infetto, non in quarantena (474). */
  documento: { id: string; antivirus: string; cancellato: boolean; valido: boolean } | null
  registrato_il: string; registrato_da: { nome: string; cognome: string } | null
  /** Nome di chi ha firmato al posto della persona; nullo sulla scheda letta non in chiaro. */
  firmatario_nome: string | null
  /** Documento che prova il potere di firma di chi ha firmato al posto della persona. */
  giustificativo: { id: string; tipo: string; antivirus: string; cancellato: boolean } | null
  giustificativo_richiesto: boolean
  /** Firmata da un terzo senza un giustificativo valido. */
  da_completare: boolean
}

/**
 * Firma di un terzo a cui manca il giustificativo, una per sottoscrizione. `consenso_id` e' la riga
 * da indicare a PUT /persone/:id/consensi/:consenso/giustificativo, che completa tutta la firma.
 */
export type FirmaDaCompletare = {
  consenso_id: string
  sottoscrizione_id: string | null
  firmatario: string
  firmatario_nome: string | null
  /** Firma registrata senza il nome di chi ha firmato: si chiede completandola. */
  nome_mancante: boolean
  revoca: boolean
  data: string | null
  dichiarazioni: { tipo: string; numero: number | null; nome: string | null; esito: string }[]
  vigente: boolean
  /** Il documento collegato e' stato cancellato o e' risultato infetto. */
  giustificativo_da_sostituire: boolean
  /** Ultimo giorno per completare la firma (AAAA-MM-GG): trenta giorni dalla registrazione, salvo impostazione. */
  completare_entro: string
  /** Termine passato: ne viene avvisata anche la Direzione. Gli interventi non si bloccano. */
  termine_scaduto: boolean
}

export type VoceVigente = {
  tipo: TipoRev05; numero: number; nome: string; necessario_interventi: boolean
  esito: EsitoVigente; consenso: ConsensoLetto | null
}

export type IndicatoreConsensi = {
  scheda_migrata: boolean
  registrazione_interventi: 'ammessa' | 'avviso' | 'bloccata'
  voci: { tipo: TipoRev05; numero: number; nome: string; esito: EsitoVigente; data: string | null }[]
  conteggi: { espressi: number; negati: number; revocati: number; mancanti: number }
}

/** Dichiarazione in vigore coperta da un modulo firmato, o in attesa di esserlo. */
export type DichiarazioneFirmata = { tipo: string; numero: number | null; nome: string | null; esito: string }

/**
 * Modulo firmato dei consensi in vigore (474, osservazioni n. 4 del 23/10/2026, punto 12): un solo foglio
 * con tutte le dichiarazioni, caricato una volta. Stessa regola di apps/api/src/servizi/consensi-rev05.ts.
 */
export type ModuloFirmato = {
  /** Firme senza modulo valido: «Carica il modulo firmato» le completa tutte insieme. */
  da_collegare: { firma: string; sottoscrizione_id: string | null; data: string | null; dichiarazioni: DichiarazioneFirmata[] }[]
  /** Moduli validi collegati alle dichiarazioni in vigore, uno per documento. */
  collegati: { id: string; antivirus: string; dichiarazioni: DichiarazioneFirmata[] }[]
}

/** Consensi della scheda: in GET /persone/:id con `consenso.leggi` sulla scheda, e in GET /persone/:id/consensi. */
export type ConsensiLetti = {
  vigenti: VoceVigente[]; obbligatori: IndicatoreConsensi; storico: ConsensoLetto[]
  da_completare: FirmaDaCompletare[]
  modulo_firmato: ModuloFirmato
}

/**
 * Tipo con cui si carica la scansione del modulo firmato (core.documento.tipo). Come modulo vale anche
 * un documento gia' caricato di un altro tipo che il catalogo ammette (`modulo_consensi`, 474).
 */
export const TIPO_DOCUMENTO_FIRMATO = 'SCHEDA_UTENTE_FIRMATA'

/**
 * Chi firma il modulo. `etichetta` e' la voce della scelta; `breve`, dove c'e', e' il nome nelle frasi
 * e nelle tabelle, quando la spiegazione tra parentesi appesantirebbe.
 */
export const FIRMATARI: { valore: string; etichetta: string; breve?: string }[] = [
  { valore: 'interessato', etichetta: 'la persona interessata' },
  { valore: 'genitore', etichetta: 'un genitore' },
  { valore: 'tutore', etichetta: 'il tutore' },
  { valore: 'amministratore_sostegno', etichetta: 'l’amministratore di sostegno' },
  // Poteri tutelari sul minore affidato alla struttura fino alla nomina del tutore (456).
  {
    valore: 'responsabile_struttura',
    etichetta: 'il responsabile della struttura di accoglienza (poteri tutelari fino alla nomina del tutore)',
    breve: 'il responsabile della struttura di accoglienza',
  },
]

export const nomeFirmatario = (codice: string | null | undefined) => {
  const voce = FIRMATARI.find((f) => f.valore === codice)
  return voce ? voce.breve ?? voce.etichetta : codice ?? ''
}

/** Qualita' di chi firma, per la riga «Nome e qualità del firmatario» del modulo stampato. */
const QUALITA: Record<string, string> = {
  interessato: 'persona interessata',
  genitore: 'genitore',
  tutore: 'tutore',
  amministratore_sostegno: 'amministratore di sostegno',
  responsabile_struttura: 'responsabile della struttura di accoglienza',
}

/** Chi ha firmato per ultimo i consensi, come lo restituisce GET /persone/:id/stampa. */
export type FirmatarioNoto = { firmatario: string; nome: string | null; data: string | null }

/**
 * Riga «Nome e qualità del firmatario» compilata: «Mario Rossi, tutore». Per la persona interessata il
 * nome e' quello della scheda. Nulla se il nome non e' noto: la riga resta da compilare a mano.
 */
export function rigaFirmatario(
  noto: FirmatarioNoto | null | undefined, scheda: { nome?: string | null; cognome?: string | null },
): string | null {
  if (!noto || !QUALITA[noto.firmatario]) return null
  const nome = noto.firmatario === 'interessato'
    ? `${scheda.nome ?? ''} ${scheda.cognome ?? ''}`.trim()
    : noto.nome?.trim() ?? ''
  return nome ? `${nome}, ${QUALITA[noto.firmatario]}` : null
}

// --- firma di un terzo (giustificativo del potere di firma) -----------------------------------

/**
 * Firmatari che firmano al posto della persona. Per loro si registra il nome di chi firma e serve il
 * documento che ne prova il potere di firma; senza, il consenso vale e la firma resta da completare.
 * Stesso elenco di apps/api/src/servizi/consensi-rev05.ts.
 */
export const FIRMATARI_TERZI = ['genitore', 'tutore', 'amministratore_sostegno', 'responsabile_struttura'] as const

export const firmaDiTerzi = (firmatario: string | null | undefined) =>
  (FIRMATARI_TERZI as readonly string[]).includes(firmatario ?? '')

/** Le forme del firmatario nelle frasi e il documento che ne prova il potere di firma. */
const FIRMA_DI_TERZI: Record<string, { da: string; di: string; spiegazione: string }> = {
  genitore: {
    da: 'da un genitore',
    di: 'di un genitore',
    spiegazione: 'Serve un documento che provi la responsabilità genitoriale: per esempio l’estratto dell’atto di '
      + 'nascita, il certificato di stato di famiglia o un provvedimento del giudice.',
  },
  tutore: {
    da: 'dal tutore',
    di: 'del tutore',
    spiegazione: 'Serve il decreto con cui il tribunale apre la tutela o nomina il tutore.',
  },
  amministratore_sostegno: {
    da: 'dall’amministratore di sostegno',
    di: 'dell’amministratore di sostegno',
    spiegazione: 'Serve il decreto con cui il giudice tutelare nomina l’amministratore di sostegno.',
  },
  responsabile_struttura: {
    da: 'dal responsabile della struttura di accoglienza',
    di: 'del responsabile della struttura di accoglienza',
    spiegazione: 'Serve il provvedimento con cui il minore è stato affidato alla struttura (art. 403 del codice '
      + 'civile), adottato dalle forze dell’ordine o dall’ente locale. Il responsabile firma finché il tribunale non '
      + 'nomina il tutore; dopo la nomina firma il tutore.',
  },
}

/** «dal tutore», «da un genitore». */
export const firmatoDa = (firmatario: string) => FIRMA_DI_TERZI[firmatario]?.da ?? `da ${nomeFirmatario(firmatario)}`

/** «del tutore», «di un genitore». */
export const firmaDi = (firmatario: string) => FIRMA_DI_TERZI[firmatario]?.di ?? `di ${nomeFirmatario(firmatario)}`

/** Il documento che serve, in una frase intera con gli esempi: per l'aiuto dei campi. */
export const spiegazioneGiustificativo = (firmatario: string) =>
  FIRMA_DI_TERZI[firmatario]?.spiegazione ?? 'Serve un documento che provi il potere di firma di chi ha firmato.'

/** Voce di GET /documenti/tipi: `giustifica_firmatario` dice di quale firmatario il tipo prova il potere. */
export type TipoGiustificativo = { codice: string; nome: string; giustifica_firmatario: string | null }

/** Tipi di documento che provano il potere di firma del firmatario, nell'ordine del catalogo. */
export const tipiGiustificativo = <T extends TipoGiustificativo>(tipi: T[], firmatario: string): T[] =>
  tipi.filter((t) => t.giustifica_firmatario === firmatario)

/** Elenco di numeri in una frase: «1», «1 e 2», «1, 2 e 3». */
const elencoNumeri = (numeri: (number | null)[]) => {
  const voci = numeri.filter((n): n is number => n !== null).map(String)
  return voci.length <= 1 ? voci.join('') : `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`
}

/**
 * La firma da completare in una riga: «Modulo del 10/03/2025 firmato dal tutore, Mario Rossi
 * (dichiarazioni 1, 2 e 3)» oppure «Revoca del consenso 1 del 01/04/2025 firmata da un genitore».
 */
export function descriviFirma(f: FirmaDaCompletare): string {
  const chi = `${firmatoDa(f.firmatario)}${f.firmatario_nome ? `, ${f.firmatario_nome}` : ''}`
  const numeri = elencoNumeri(f.dichiarazioni.map((d) => d.numero))
  if (f.revoca) return `Revoca del consenso ${numeri} del ${giornoItaliano(f.data)} firmata ${chi}`
  const quali = f.dichiarazioni.length === 1 ? `dichiarazione ${numeri}` : `dichiarazioni ${numeri}`
  return `Modulo del ${giornoItaliano(f.data)} firmato ${chi} (${quali})`
}

/** Termine della firma in poche parole: «da completare entro il 17/11/2026» o «termine scaduto il 17/11/2026». */
export const termineFirma = (f: Pick<FirmaDaCompletare, 'completare_entro' | 'termine_scaduto'>) =>
  f.termine_scaduto
    ? `termine scaduto il ${giornoItaliano(f.completare_entro)}`
    : `da completare entro il ${giornoItaliano(f.completare_entro)}`

/** La firma da completare a cui appartiene una riga del registro: stessa sottoscrizione, o la riga stessa. */
export const firmaDelConsenso = (consensi: ConsensiLetti, c: ConsensoLetto): FirmaDaCompletare | undefined =>
  consensi.da_completare.find((f) => f.consenso_id === c.id
    || (!!c.sottoscrizione_id && f.sottoscrizione_id === c.sottoscrizione_id))

/** Esito vigente in parole: il consenso non raccolto non si confonde con il diniego. */
export const ETICHETTE_ESITO: Record<EsitoVigente, string> = {
  espresso: 'espresso', negato: 'negato', revocato: 'revocato', mancante: 'non raccolto',
}

/** Righe del registro precedente, che restano nello storico e non valgono come consensi Rev05. */
const TIPI_STORICI: Record<string, string> = {
  servizio: 'Consenso del vecchio archivio',
  dati_particolari: 'Dati particolari (registro precedente)',
  comunicazione_capofila: 'Comunicazione ai capofila (registro precedente)',
  follow_up: 'Follow up (registro precedente)',
  immagini: 'Immagini (registro precedente)',
  ricerca: 'Ricerca (registro precedente)',
}

const STATI: Record<string, string> = {
  ...ETICHETTE_ESITO,
  raccolto: 'raccolto',
  migrato: 'spunta presente nel vecchio archivio',
  non_documentato: 'spunta assente nel vecchio archivio',
}

export const nomeTipoConsenso = (riga: Pick<ConsensoLetto, 'tipo' | 'numero' | 'nome'>) =>
  riga.nome ? `${riga.numero}. ${riga.nome}` : TIPI_STORICI[riga.tipo] ?? riga.tipo

export const nomeStatoConsenso = (stato: string) => STATI[stato] ?? stato

/** Data AAAA-MM-GG come GG/MM/AAAA. */
export const giornoItaliano = (giorno: string | null | undefined) => (giorno ? giorno.slice(0, 10).split('-').reverse().join('/') : '')

// --- sottoscrizione ----------------------------------------------------------------------------

export type StatoSottoscrizione = {
  /** Esito scelto per tipo; vuoto se la dichiarazione non ha una scelta. */
  esiti: Record<string, '' | Esito>
  luogo: string
  data: string
  firmatario: string
  /** Nome e cognome di chi firma al posto della persona; con l'interessato non si invia. */
  firmatario_nome?: string
  presa_visione: boolean
}

export const sottoscrizioneNuova = (oggi: string): StatoSottoscrizione => ({
  esiti: {}, luogo: '', data: oggi, firmatario: 'interessato', firmatario_nome: '', presa_visione: false,
})

/**
 * Nella creazione della scheda la sezione e' facoltativa: si considera compilata appena si sceglie
 * un esito, si scrive il luogo o il nome di chi firma per un altro, o si conferma la presa visione.
 * Data e firmatario hanno un valore iniziale e da soli non la aprono.
 */
export const sottoscrizioneIniziata = (s: StatoSottoscrizione) =>
  Object.values(s.esiti).some(Boolean) || !!s.luogo.trim() || s.presa_visione
  || (firmaDiTerzi(s.firmatario) && !!s.firmatario_nome?.trim())

/** Nome di chi firma per un altro: obbligatorio con un firmatario terzo, come nell'API. */
function erroreNomeFirmatario(firmatario: string, nome: string | undefined): string | null {
  if (!firmaDiTerzi(firmatario)) return null
  const testo = (nome ?? '').trim()
  if (!testo) return 'Indicare nome e cognome di chi firma al posto della persona.'
  if (testo.length > 120) return 'Al massimo 120 caratteri.'
  return null
}

/** Campi della firma di un terzo nel corpo: nome e giustificativo solo se non firma l'interessato. */
const campiFirmaDiTerzi = (firmatario: string, nome: string | undefined, giustificativoId: string | null | undefined) =>
  firmaDiTerzi(firmatario)
    ? { firmatario_nome: (nome ?? '').trim(), ...(giustificativoId ? { giustificativo_id: giustificativoId } : {}) }
    : {}

/** Data di una firma: non dopo oggi, non prima della data minima. */
export function verificaDataFirma(data: string, oggi: string, minima: string | null, motivoMinima: string): string | null {
  if (!data) return 'Indicare la data.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return 'Data non valida.'
  if (data > oggi) return 'La data non può essere successiva a oggi.'
  if (minima && data < minima) return `La data non può precedere ${motivoMinima} (${giornoItaliano(minima)}).`
  return null
}

/**
 * Sottoscrizione del modulo: ogni dichiarazione non facoltativa ha «Esprimo» o «Non esprimo»; luogo,
 * data, firmatario e presa visione sono obbligatori. Chiavi degli errori come i campi dell'API
 * (`esiti.<tipo>`, `luogo`, `data`, `firmatario`, `presa_visione`).
 */
export function verificaSottoscrizione(s: StatoSottoscrizione, modulo: ModuloConsensi, oggi: string): Errori {
  const errori: Errori = {}
  for (const d of modulo.consensi) {
    if (!d.facoltativo && !s.esiti[d.tipo]) errori[`esiti.${d.tipo}`] = 'Indicare «Esprimo» o «Non esprimo».'
  }
  const luogo = s.luogo.trim()
  if (!luogo) errori.luogo = 'Indicare il luogo della sottoscrizione.'
  else if (luogo.length > 120) errori.luogo = 'Al massimo 120 caratteri.'
  const data = verificaDataFirma(s.data, oggi, modulo.data_revisione, 'la revisione del modulo')
  if (data) errori.data = data
  if (!FIRMATARI.some((f) => f.valore === s.firmatario)) errori.firmatario = 'Indicare chi firma il modulo.'
  const nome = erroreNomeFirmatario(s.firmatario, s.firmatario_nome)
  if (nome) errori.firmatario_nome = nome
  if (!s.presa_visione) errori.presa_visione = 'Serve la presa visione dell’informativa sottoscritta dalla persona.'
  return errori
}

/**
 * Corpo di POST /persone/:id/consensi. Con un firmatario terzo porta il nome di chi firma e, se gia'
 * caricato, il giustificativo; con l'interessato nessuno dei due, anche se scritti e poi lasciati.
 */
export function corpoSottoscrizione(
  s: StatoSottoscrizione, modulo: ModuloConsensi, documentoId?: string | null, giustificativoId?: string | null,
) {
  return {
    esiti: modulo.consensi.filter((d) => s.esiti[d.tipo]).map((d) => ({ tipo: d.tipo, esito: s.esiti[d.tipo] as Esito })),
    luogo: s.luogo.trim(),
    data: s.data,
    firmatario: s.firmatario,
    presa_visione: s.presa_visione,
    ...(documentoId ? { documento_id: documentoId } : {}),
    ...campiFirmaDiTerzi(s.firmatario, s.firmatario_nome, giustificativoId),
  }
}

// --- revoca ------------------------------------------------------------------------------------

export type StatoRevoca = { data: string; firmatario: string; luogo: string; firmatario_nome?: string }

export const revocaNuova = (oggi: string): StatoRevoca => ({ data: oggi, firmatario: 'interessato', luogo: '', firmatario_nome: '' })

export function verificaRevoca(s: StatoRevoca, oggi: string, dataConsenso: string | null): Errori {
  const errori: Errori = {}
  const data = verificaDataFirma(s.data, oggi, dataConsenso, 'il consenso revocato')
  if (data) errori.data = data
  if (!FIRMATARI.some((f) => f.valore === s.firmatario)) errori.firmatario = 'Indicare chi firma il modulo.'
  const nome = erroreNomeFirmatario(s.firmatario, s.firmatario_nome)
  if (nome) errori.firmatario_nome = nome
  if (s.luogo.trim().length > 120) errori.luogo = 'Al massimo 120 caratteri.'
  return errori
}

export function corpoRevoca(s: StatoRevoca, tipo: TipoRev05, documentoId?: string | null, giustificativoId?: string | null) {
  return {
    tipo, data: s.data, firmatario: s.firmatario,
    ...(s.luogo.trim() ? { luogo: s.luogo.trim() } : {}),
    ...(documentoId ? { documento_id: documentoId } : {}),
    ...campiFirmaDiTerzi(s.firmatario, s.firmatario_nome, giustificativoId),
  }
}

// --- completamento della firma: percorso guidato --------------------------------------------

/** Passi del percorso che completa la firma di un terzo, nell'ordine. */
export const PASSI_COMPLETAMENTO = ['Carica il documento', 'Scegli il tipo', 'Conferma'] as const

export type StatoCompletamento = {
  /** File da caricare adesso, oppure documento gia' tra quelli della scheda; vuoto finche' non si sceglie. */
  origine: '' | 'nuovo' | 'esistente'
  documento_esistente: string
  /** Tipo del file nuovo, dal catalogo; preimpostato se per quel firmatario ce n'e' uno solo. */
  tipo: string
  data_documento: string
  scadenza: string
  /** Solo per le firme registrate senza il nome di chi ha firmato. */
  firmatario_nome: string
}

/** Con documenti gia' adatti nella scheda si chiede da dove viene il documento; altrimenti si carica. */
export const completamentoNuovo = (tipiAmmessi: TipoGiustificativo[], conEsistenti: boolean): StatoCompletamento => ({
  origine: conEsistenti ? '' : 'nuovo', documento_esistente: '',
  tipo: tipiAmmessi.length === 1 ? tipiAmmessi[0].codice : '',
  data_documento: '', scadenza: '', firmatario_nome: '',
})

/**
 * Verifiche di un passo prima di andare avanti. Passo 0: il documento (file scelto o documento della
 * scheda). Passo 1: il tipo del file nuovo tra quelli ammessi, le date e, se manca, il nome di chi ha
 * firmato. Il terzo passo e' la conferma e non ha campi.
 */
export function verificaPassoCompletamento(
  passo: number, s: StatoCompletamento,
  contesto: { file: boolean; tipiAmmessi: TipoGiustificativo[]; nomeMancante: boolean; oggi: string },
): Errori {
  const errori: Errori = {}
  if (passo === 0) {
    if (!s.origine) errori.origine = 'Indicare se caricare un file o usare un documento già presente nella scheda.'
    else if (s.origine === 'nuovo' && !contesto.file) errori.giustificativo = 'Scegliere il file del documento.'
    else if (s.origine === 'esistente' && !s.documento_esistente) {
      errori.documento_esistente = 'Scegliere uno dei documenti della scheda.'
    }
  }
  if (passo === 1) {
    if (s.origine === 'nuovo') {
      if (!contesto.tipiAmmessi.some((t) => t.codice === s.tipo)) errori.tipo = 'Indicare che documento è.'
      if (s.data_documento && s.data_documento > contesto.oggi) {
        errori.data_documento = 'La data del documento non può essere successiva a oggi.'
      }
      if (s.scadenza && s.data_documento && s.scadenza < s.data_documento) {
        errori.scadenza = 'La scadenza non può precedere la data del documento.'
      }
    }
    if (contesto.nomeMancante) {
      const testo = s.firmatario_nome.trim()
      if (!testo) errori.firmatario_nome = 'Indicare nome e cognome di chi ha firmato al posto della persona.'
      else if (testo.length > 120) errori.firmatario_nome = 'Al massimo 120 caratteri.'
    }
  }
  return errori
}

/** Corpo di PUT /persone/:id/consensi/:consenso/giustificativo: il nome solo se la firma non lo ha. */
export const corpoGiustificativo = (s: StatoCompletamento, documentoId: string, nomeMancante: boolean) => ({
  documento_id: documentoId,
  ...(nomeMancante ? { firmatario_nome: s.firmatario_nome.trim() } : {}),
})

/** Passo in cui si corregge un errore dell'API sul percorso: il documento al primo, il nome al secondo. */
export const passoDellErrore = (errori: Errori): number | null =>
  errori.giustificativo || errori.documento_esistente ? 0 : errori.firmatario_nome || errori.tipo ? 1 : null

// --- modulo firmato unico (474) --------------------------------------------------------------

/** Voce di GET /documenti/tipi: `modulo_consensi` dice se il tipo vale come modulo firmato dei consensi. */
export type TipoModulo = { codice: string; nome: string; modulo_consensi?: boolean }

/** Tipi di documento che valgono come modulo firmato, nell'ordine del catalogo. */
export const tipiModulo = <T extends TipoModulo>(tipi: T[]): T[] => tipi.filter((t) => t.modulo_consensi)

/** «dichiarazione 1» o «dichiarazioni 1, 2 e 3». */
const dichiarazioni = (voci: DichiarazioneFirmata[]) => {
  const numeri = elencoNumeri(voci.map((d) => d.numero))
  return voci.length === 1 ? `dichiarazione ${numeri}` : `dichiarazioni ${numeri}`
}

/**
 * Le dichiarazioni in vigore che il modulo firmato coprira', in una frase: «dichiarazioni 1, 2, 3 e 4
 * del 10/03/2025», o piu' gruppi separati da punto e virgola se vengono da firme diverse.
 */
export const descriviDaCollegare = (m: ModuloFirmato) =>
  m.da_collegare.map((f) => `${dichiarazioni(f.dichiarazioni)}${f.data ? ` del ${giornoItaliano(f.data)}` : ''}`).join('; ')

/** Stato del modulo firmato in poche parole, per la sezione Consensi. Nullo se non ci sono consensi in vigore. */
export function statoModuloFirmato(m: ModuloFirmato | undefined): 'da_caricare' | 'collegato' | null {
  if (!m) return null
  if (m.da_collegare.length) return 'da_caricare'
  return m.collegati.length ? 'collegato' : null
}

export type StatoModulo = {
  /** File da caricare adesso, oppure documento gia' tra quelli della scheda; vuoto finche' non si sceglie. */
  origine: '' | 'nuovo' | 'esistente'
  documento_esistente: string
}

/** Con documenti gia' adatti nella scheda si chiede da dove viene il modulo; altrimenti si carica. */
export const moduloNuovo = (conEsistenti: boolean): StatoModulo => ({ origine: conEsistenti ? '' : 'nuovo', documento_esistente: '' })

/** Verifica prima dell'invio: un file scelto, oppure uno dei documenti della scheda. */
export function verificaModulo(s: StatoModulo, contesto: { file: boolean }): Errori {
  if (!s.origine) return { origine: 'Indicare se caricare un file o usare un documento già presente nella scheda.' }
  if (s.origine === 'nuovo' && !contesto.file) return { documento: 'Scegliere il file del modulo firmato.' }
  if (s.origine === 'esistente' && !s.documento_esistente) return { documento_esistente: 'Scegliere uno dei documenti della scheda.' }
  return {}
}

/** Corpo di PUT /persone/:id/consensi/modulo-firmato. */
export const corpoModulo = (documentoId: string) => ({ documento_id: documentoId })

// --- errori dell'API ---------------------------------------------------------------------------

/**
 * Errori dell'API sui campi dei moduli dei consensi: i dettagli di validazione portano gia' il nome
 * del campo (`esiti.TRATTAMENTO_DATI`, `data`, `firmatario_nome`, `giustificativo`); il documento non
 * valido va sul campo della scansione, il giustificativo non valido sul suo. Vuoto se l'errore non
 * riguarda un campo (perimetro, guasto): va mostrato in testa.
 */
export function erroriDaApi(errore: ErroreRicevuto): Errori {
  const errori: Errori = {}
  for (const d of errore.dettagli ?? []) {
    const campo = /^esiti\.\d+/.test(d.campo) ? 'esiti' : d.campo === 'giustificativo_id' ? 'giustificativo' : d.campo
    errori[campo] ??= d.messaggio
  }
  if (errore.codice === 'DOCUMENTO_NON_VALIDO') errori.documento = errore.message
  if (errore.codice === 'GIUSTIFICATIVO_NON_VALIDO') errori.giustificativo = errore.message
  return errori
}

/** Sezione dei consensi registrata alla creazione (avviso di Scheda.tsx). Senza `consenso.leggi` non si sa: non si segnala. */
export const consensiRegistrati = (consensi: ConsensiLetti | undefined) =>
  consensi === undefined || consensi.storico.some((r) => r.rev05)
